import { Button, Col, Row } from 'reactstrap';
import React, { Component } from 'react';
import { callApi, formatDate, formatSignedAmount } from '../../utils';

import CreateCustomerModal from '../CreateCustomerModal';
import DataTable, { PageHeader, EmptyState } from '../DataTable';
import ErrorTick from '../ErrorTick';
import FilterBuilder, { buildWhere } from '../FilterBuilder';
import FilterPanel, { whereToConditions } from '../SavedFilters/FilterPanel';
import { createSavedFilter } from '../../services/savedFiltersApi';
import SuccessTick from '../SuccessTick';
import { connect } from 'react-redux';
import { openCreateCustomerModal } from '../../actions/modalActions';
import { setCustomerDetails } from '../../actions/customerAction';
import { showError } from '../../actions/notificationActions';

// ─── Field definitions ────────────────────────────────────────────────────────

const USER_FIELDS = [
  { key: 'name', label: 'Name', type: 'string' },
  { key: 'phone', label: 'Phone', type: 'string' },
  { key: 'email', label: 'Email', type: 'string' },
  { key: 'created_at', label: 'Joined', type: 'date' },
  { key: 'social_follow', label: 'Social Follow', type: 'boolean' },
  { key: 'deleted_at', label: 'Deleted At', type: 'date' },
];

// ─── URL <-> filter state helpers ─────────────────────────────────────────────

function encodeConditions(conditions) {
  return encodeURIComponent(
    JSON.stringify(conditions.map(({ id, ...rest }) => rest))
  );
}

function parseUrlParams(search) {
  const params = new URLSearchParams(search);
  let conditions = [];
  let logicalOp = '$and';
  let page = 1;

  const raw = params.get('c');
  if (raw) {
    try {
      const parsed = JSON.parse(decodeURIComponent(raw));
      conditions = parsed.map((c, i) => ({ ...c, id: Date.now() + i }));
    } catch (_) {}
  }

  const op = params.get('op');
  if (op === '$and' || op === '$or') logicalOp = op;

  const p = parseInt(params.get('page'), 10);
  if (p > 0) page = p;

  return { conditions, logicalOp, page };
}

// ─── Component ────────────────────────────────────────────────────────────────

class Users extends Component {
  constructor(props) {
    super(props);
    const { conditions, logicalOp, page } = parseUrlParams(
      props.location ? props.location.search : ''
    );
    this.state = {
      users: [],
      conditions,
      logicalOp,
      page,
      limit: 30,
      loading: false,
      activeSavedFilter: null,
      builderKey: 0,
    };
  }

  componentDidMount() {
    if (this.state.conditions.length > 0) {
      this.loadUsers();
    }
  }

  syncUrl = (conditions, logicalOp, page) => {
    const params = new URLSearchParams();
    if (conditions.length > 0) {
      params.set('c', encodeConditions(conditions));
      params.set('op', logicalOp);
    }
    if (page > 1) params.set('page', String(page));

    const search = params.toString();
    const current = this.props.location
      ? this.props.location.search.replace(/^\?/, '')
      : '';
    if (search !== current) {
      this.props.history.replace({ search: search ? `?${search}` : '' });
    }
  };

  loadUsers = () => {
    const { conditions, logicalOp, page, limit } = this.state;
    const where = buildWhere(conditions, logicalOp);
    const hasFilter = Object.keys(where).length > 0;

    const qs = [
      `page=${page}`,
      `limit=${limit}`,
      hasFilter ? `where=${encodeURIComponent(JSON.stringify(where))}` : '',
    ]
      .filter(Boolean)
      .join('&');

    this.setState({ loading: true });
    callApi(`/users?${qs}`)
      .then(({ data = [] }) => {
        this.setState({ loading: false, users: data });
        setTimeout(() => window.scrollTo(0, 0), 200);
      })
      .catch((err) => {
        this.setState({ loading: false });
        this.props.dispatch(showError(err));
      });
  };

  handleFilterChange = (_where, conditions, logicalOp) => {
    this.setState(
      { conditions, logicalOp, page: 1, activeSavedFilter: null },
      () => {
        this.syncUrl(conditions, logicalOp, 1);
        if (conditions.length > 0) {
          this.loadUsers();
        } else {
          this.setState({ users: [] });
        }
      }
    );
  };

  handleSaveFilter = ({ label, description }) => {
    const { conditions, logicalOp } = this.state;
    const where = buildWhere(conditions, logicalOp);
    if (Object.keys(where).length === 0) return;
    createSavedFilter('users', {
      type: 'custom',
      label,
      description,
      value: JSON.stringify(where),
    })
      .then(({ data }) => {
        this.setState({ activeSavedFilter: data });
      })
      .catch((err) => {
        this.props.dispatch(showError(err));
      });
  };

  handleApplySavedFilter = (filter) => {
    const parsed = whereToConditions(filter.value, USER_FIELDS);
    const conditions = parsed.conditions.map((c, i) => ({
      ...c,
      id: Date.now() + i,
    }));
    const logicalOp = parsed.logicalOp;
    this.setState(
      function (prev) {
        return {
          conditions,
          logicalOp,
          activeSavedFilter: filter,
          page: 1,
          builderKey: prev.builderKey + 1,
        };
      },
      () => {
        this.syncUrl(conditions, logicalOp, 1);
        if (conditions.length > 0) this.loadUsers();
      }
    );
  };

  handleSavedFilterUpdated = (filter) => {
    this.setState(function (prev) {
      var active = prev.activeSavedFilter;
      return {
        activeSavedFilter: active && active.id === filter.id ? filter : active,
      };
    });
  };

  handleDeletedSavedFilter = (filterId) => {
    this.setState(function (prev) {
      var active = prev.activeSavedFilter;
      return {
        activeSavedFilter: active && active.id === filterId ? null : active,
      };
    });
  };

  clearFilters = () => {
    this.setState(
      {
        conditions: [],
        logicalOp: '$and',
        page: 1,
        activeSavedFilter: null,
        users: [],
      },
      () => this.syncUrl([], '$and', 1)
    );
  };

  setPage = (page) => {
    if (!page) return;
    this.setState({ page }, () => {
      this.syncUrl(this.state.conditions, this.state.logicalOp, page);
      this.loadUsers();
    });
  };

  openUser = (user) => {
    this.props.history.push(`/users/${user.id}`);
    this.props.dispatch(setCustomerDetails(user));
  };

  refresh = () => window.location.reload();

  render() {
    const {
      users,
      conditions,
      logicalOp,
      page,
      loading,
      limit,
      activeSavedFilter,
      builderKey,
    } = this.state;
    const hasFilter = conditions.length > 0;
    const hasResults = users.length > 0;

    return (
      <div className="container-fluid">
        <CreateCustomerModal refresh={this.refresh} />

        <Row className="mt-3 mt-md-4">
          <Col xs={12}>
            {/* ── Page header ───────────────────────────────────────────────── */}
            <PageHeader
              title="USERS"
              subtitle={
                hasFilter
                  ? 'Filtered results — page ' + page
                  : 'All Users — page ' + page
              }
            >
              <Button
                color="primary"
                size="sm"
                onClick={() => this.props.dispatch(openCreateCustomerModal())}
              >
                + Create User
              </Button>
            </PageHeader>

            {/* ── Filter builder ─────────────────────────────────────────────── */}
            <div style={{ marginBottom: 16 }}>
              <FilterBuilder
                key={builderKey}
                fields={USER_FIELDS}
                initialConditions={conditions}
                initialLogicalOp={logicalOp}
                onChange={this.handleFilterChange}
                onSaveFilter={this.handleSaveFilter}
              />
            </div>

            {/* ── No filter: SavedFilters panel as placeholder ───────────────── */}
            {!hasFilter && (
              <FilterPanel
                inline
                resource="users"
                fields={USER_FIELDS}
                activeSavedFilter={activeSavedFilter}
                onApplySavedFilter={this.handleApplySavedFilter}
                onSavedFilterUpdated={this.handleSavedFilterUpdated}
                onDeletedSavedFilter={this.handleDeletedSavedFilter}
              />
            )}

            {/* ── Filter applied: results table ─────────────────────────────── */}
            {hasFilter && (
              <DataTable
                minWidth={540}
                loading={loading}
                page={page}
                onPageChange={this.setPage}
                showPagination={hasResults}
              >
                <thead className="text-primary">
                  <tr>
                    <th>#</th>
                    <th>Name</th>
                    <th>Phone</th>
                    <th>Email</th>
                    <th>Joined</th>
                    <th>Social</th>
                    <th>Wallet</th>
                  </tr>
                </thead>

                {!loading && !hasResults ? (
                  <EmptyState
                    colSpan={7}
                    heading="No users match your filters"
                    message="Try adjusting or removing one of the conditions above."
                    actionLabel="Clear filters"
                    onAction={this.clearFilters}
                  />
                ) : (
                  <tbody>
                    {users.map((u, i) => (
                      <tr
                        key={u.id}
                        onClick={() => this.openUser(u)}
                        style={{
                          cursor: 'pointer',
                          backgroundColor: u.deleted_at ? '#c0392b' : 'inherit',
                          color: u.deleted_at ? '#fff' : 'inherit',
                          textDecoration: u.deleted_at
                            ? 'line-through'
                            : 'none',
                        }}
                      >
                        <td>{loading ? '—' : i + 1 + (page - 1) * limit}</td>
                        <td>{u.name}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>{u.phone}</td>
                        <td
                          style={{
                            maxWidth: 180,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {u.email}
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          {formatDate(u.created_at)}
                        </td>
                        <td>
                          {Number(u.social_follow) ? (
                            <ErrorTick />
                          ) : (
                            <SuccessTick />
                          )}
                        </td>
                        {/* Wallets carry an overdraft facility, so a
                            balance can be negative — that negative IS the
                            debt. Render it signed rather than as
                            "₦-1,500.00". */}
                        <td
                          style={{ whiteSpace: 'nowrap' }}
                          className={
                            u.userWallet && Number(u.userWallet.balance) < 0
                              ? 'text-danger'
                              : ''
                          }
                        >
                          {formatSignedAmount(
                            u.userWallet
                              ? Number(u.userWallet.balance) / 100
                              : 0
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                )}
              </DataTable>
            )}
          </Col>
        </Row>
      </div>
    );
  }
}

export default connect()(Users);
